import { BaseService, ServiceResponse } from '@/services/api/base.service';
import {
  Exercise,
  ExerciseAssignment,
  CreateExerciseInput,
  UpdateExerciseInput,
  ExerciseFilters,
  CreateAssignmentInput,
  UpdateAssignmentInput,
  AssignmentFilters,
  PlayerVideoSubmission,
  CreatePlayerVideoInput
} from '@/types/coaching.types';

class ExercisesService extends BaseService {
  /**
   * Get exercise library for a club with optional filters
   */
  async getExercises(
    clubId: string | null | undefined,
    filters?: ExerciseFilters
  ): Promise<ServiceResponse<Exercise[]>> {
    return this.withErrorHandling(async () => {
      // Show exercises from the club OR public exercises from any club
      let query = this.supabase
        .from('exercises')
        .select('*');
      
      // Build the OR condition: club exercises OR public exercises
      if (clubId) {
        query = query.or(`club_id.eq.${clubId},is_public.eq.true`);
      } else {
        // If no club ID, only show public exercises
        query = query.eq('is_public', true);
      }
      
      query = query.order('created_at', { ascending: false });

      // Apply filters
      if (filters?.category) {
        query = query.eq('category', filters.category);
      }
      if (filters?.practice_category) {
        query = query.eq('practice_category', filters.practice_category);
      }
      if (filters?.difficulty) {
        query = query.eq('difficulty', filters.difficulty);
      }
      if (filters?.search) {
        const q = filters.search.replace(/%/g, '\\%').replace(/_/g, '\\_');
        query = query.or(`name.ilike.%${q}%,description.ilike.%${q}%`);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data || [];
    });
  }

  /**
   * Get a single exercise by ID
   */
  async getExercise(id: string): Promise<ServiceResponse<Exercise>> {
    return this.withErrorHandling(async () => {
      const { data, error } = await this.supabase
        .from('exercises')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;
      return data;
    });
  }

  /**
   * Create a new exercise
   */
  async createExercise(
    input: CreateExerciseInput
  ): Promise<ServiceResponse<Exercise>> {
    return this.withErrorHandling(async () => {
      const { data: authData, error: authError } = await this.supabase.auth.getUser();
      if (authError) throw authError;
      const userId = authData.user?.id;
      if (!userId) throw new Error('Not authenticated');

      const { data, error } = await this.supabase
        .from('exercises')
        .insert({
          club_id: input.club_id ?? null,
          created_by: userId,
          name: input.name,
          description: input.description,
          category: input.category,
          difficulty: input.difficulty,
          duration_mins: input.duration_mins,
          equipment_needed: input.equipment_needed ?? [],
          video_url: input.video_url ?? null,
          repetitions: input.repetitions ?? null,
          is_public: input.is_public ?? false,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    });
  }

  /**
   * Update an existing exercise
   */
  async updateExercise(
    id: string,
    input: UpdateExerciseInput
  ): Promise<ServiceResponse<Exercise>> {
    return this.withErrorHandling(async () => {
      const updateData: Record<string, any> = {};

      if (input.name !== undefined) updateData.name = input.name;
      if (input.description !== undefined) updateData.description = input.description;
      if (input.category !== undefined) updateData.category = input.category;
      if (input.difficulty !== undefined) updateData.difficulty = input.difficulty;
      if (input.duration_mins !== undefined) updateData.duration_mins = input.duration_mins;
      if (input.equipment_needed !== undefined) updateData.equipment_needed = input.equipment_needed;
      if (input.video_url !== undefined) updateData.video_url = input.video_url;
      if (input.repetitions !== undefined) updateData.repetitions = input.repetitions;
      if (input.is_public !== undefined) updateData.is_public = input.is_public;

      const { data, error } = await this.supabase
        .from('exercises')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    });
  }

  /**
   * Delete an exercise
   */
  async deleteExercise(id: string): Promise<ServiceResponse<void>> {
    return this.withErrorHandling(async () => {
      const { error } = await this.supabase
        .from('exercises')
        .delete()
        .eq('id', id);

      if (error) throw error;
    });
  }

  /**
   * Assign an exercise to a player
   */
  async assignExercise(
    input: CreateAssignmentInput
  ): Promise<ServiceResponse<ExerciseAssignment>> {
    return this.withErrorHandling(async () => {
      const { data, error } = await this.supabase
        .from('exercise_assignments')
        .insert({
          exercise_id: input.exercise_id,
          player_id: input.player_id,
          coach_id: input.coach_id ?? null, // Allow null for self-assigned exercises
          club_id: input.club_id ?? null,
          assigned_at: new Date().toISOString(),
          due_date: input.due_date ?? null,
          status: 'pending',
          coach_notes: input.coach_notes ?? null,
          // Override fields (NEW - for customizing assignments per player)
          equipment_override: input.equipment_override ?? null,
          repetitions_override: input.repetitions_override ?? null,
          duration_override: input.duration_override ?? null,
          target_override: input.target_override ?? null,
          instructions_override: input.instructions_override ?? null,
          // Attached instructional video from coach's library
          video_id: input.video_id ?? null,
        })
        .select(`
          *,
          exercise:exercises(*),
          player:profiles!exercise_assignments_player_id_fkey(id, display_name, avatar_url, email),
          coach:coach_profiles(id, user_id, bio),
          video:video_content(*)
        `)
        .single();

      if (error) throw error;
      return data;
    });
  }

  /**
   * Update an exercise assignment
   */
  async updateAssignment(
    id: string,
    input: UpdateAssignmentInput
  ): Promise<ServiceResponse<ExerciseAssignment>> {
    return this.withErrorHandling(async () => {
      const updateData: Record<string, any> = {};

      if (input.status !== undefined) updateData.status = input.status;
      if (input.player_notes !== undefined) updateData.player_notes = input.player_notes;
      if (input.player_video_url !== undefined) updateData.player_video_url = input.player_video_url;
      if (input.player_video_submitted_at !== undefined) updateData.player_video_submitted_at = input.player_video_submitted_at;
      if (input.due_date !== undefined) updateData.due_date = input.due_date;

      if (input.status === 'completed') {
        updateData.completed_at = new Date().toISOString();
      }

      const { data, error } = await this.supabase
        .from('exercise_assignments')
        .update(updateData)
        .eq('id', id)
        .select(`
          *,
          exercise:exercises(*),
          player:profiles!exercise_assignments_player_id_fkey(id, display_name, avatar_url, email),
          coach:coach_profiles!exercise_assignments_coach_id_fkey(id, user_id, bio)
        `)
        .single();

      if (error) throw error;

      // Award XP when assignment moves to completed — idempotent via UNIQUE(assignment_id)
      if (input.status === 'completed' && data) {
        const durationMins =
          (data as any).duration_override ?? (data as any).exercise?.duration_mins ?? 0;
        await this.supabase
          .from('exercise_xp_events')
          .insert({
            user_id: (data as any).player_id,
            assignment_id: (data as any).id,
            exercise_id: (data as any).exercise_id,
            duration_mins: durationMins,
            xp_earned: durationMins * 2,
            awarded_at: (data as any).completed_at,
          });
        // Conflict on assignment_id means XP was already awarded — safe to ignore
      }

      return data;
    });
  }

  /**
   * Get exercises assigned to a player
   */
  async getPlayerExercises(
    playerProfileId: string,
    filters?: AssignmentFilters
  ): Promise<ServiceResponse<ExerciseAssignment[]>> {
    return this.withErrorHandling(async () => {
      let query = this.supabase
        .from('exercise_assignments')
        .select(`
          *,
          exercise:exercises(*),
          coach:coach_profiles!exercise_assignments_coach_id_fkey(id, user_id, bio),
          video:video_content(*)
        `)
        .eq('player_id', playerProfileId)
        .order('assigned_at', { ascending: false });

      // Apply filters
      if (filters?.status) {
        if (filters.status !== 'all') query = query.eq('status', filters.status);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data || [];
    });
  }

  /**
   * Get assignments created by a coach
   */
  async getCoachAssignments(
    coachProfileId: string,
    filters?: AssignmentFilters
  ): Promise<ServiceResponse<ExerciseAssignment[]>> {
    return this.withErrorHandling(async () => {
      let query = this.supabase
        .from('exercise_assignments')
        .select(`
          *,
          exercise:exercises(*),
          player:profiles!exercise_assignments_player_id_fkey(id, display_name, avatar_url, email),
          video:video_content(*)
        `)
        .eq('coach_id', coachProfileId)
        .order('assigned_at', { ascending: false });

      // Apply filters
      if (filters?.status) {
        if (filters.status !== 'all') query = query.eq('status', filters.status);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data || [];
    });
  }

  // ===========================================================================
  // Player Video Submissions (My Videos gallery)
  // ===========================================================================

  /**
   * Get all video submissions for a player
   */
  async getPlayerVideoSubmissions(
    playerId: string
  ): Promise<ServiceResponse<PlayerVideoSubmission[]>> {
    return this.withErrorHandling(async () => {
      const { data, error } = await this.supabase
        .from('player_video_submissions')
        .select('*')
        .eq('player_id', playerId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    });
  }

  /**
   * Add a new player video submission
   */
  async addPlayerVideoSubmission(
    input: CreatePlayerVideoInput
  ): Promise<ServiceResponse<PlayerVideoSubmission>> {
    return this.withErrorHandling(async () => {
      const { data: authData, error: authError } = await this.supabase.auth.getUser();
      if (authError) throw authError;
      const userId = authData.user?.id;
      if (!userId) throw new Error('Not authenticated');

      const { data, error } = await this.supabase
        .from('player_video_submissions')
        .insert({
          player_id: userId,
          coach_id: input.coach_id ?? null,
          club_id: input.club_id ?? null,
          title: input.title ?? null,
          video_url: input.video_url,
          thumbnail_url: input.thumbnail_url ?? null,
          provider: input.provider ?? null,
          source: input.source ?? 'standalone',
          exercise_assignment_id: input.exercise_assignment_id ?? null,
          notes: input.notes ?? null,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    });
  }

  /**
   * Delete a player video submission
   */
  async deletePlayerVideoSubmission(id: string): Promise<ServiceResponse<void>> {
    return this.withErrorHandling(async () => {
      const { error } = await this.supabase
        .from('player_video_submissions')
        .delete()
        .eq('id', id);

      if (error) throw error;
    });
  }

  // ===========================================================================
  // Onboarding Integration Methods
  // ===========================================================================

}

export const exercisesService = new ExercisesService();
export default exercisesService;
